import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../hooks/useTenant';
import NoiRockLogo from '../../components/NoiRockLogo';

const DeleteAccount = () => {
  const tenant = useTenant();
  const isTcn = (tenant?.effectiveTenantSlug ?? tenant?.tenantSlug) === 'tcn';
  const brandName = isTcn ? 'TCN Agent' : 'NoiRock';
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-bg)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(ellipse, var(--color-primary) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* Header — tenant-aware: TCN logo + name on tcn.noirock.app, NoiRock otherwise.
          Back arrow + brand both route to /login-authentication so the user returns
          to the right tenant's login screen. */}
      <div className="relative z-10 flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-3">
          <Link to="/login-authentication" aria-label="Back to login" className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 no-underline hover:bg-white/10 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </Link>
          <Link to="/login-authentication" className="flex items-center gap-2 no-underline">
            <NoiRockLogo size={28} />
            <span className="text-white/60 text-xs font-semibold tracking-wide">{brandName}</span>
          </Link>
        </div>
        <Link
          to="/privacy"
          className="text-white/40 text-xs hover:text-white/70 transition-colors no-underline"
        >
          Privacy Policy →
        </Link>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <div className="mb-10">
            <h1
              className="text-2xl font-bold text-white tracking-tight mb-2"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Delete Your Account
            </h1>
            <p className="text-white/30 text-sm">Request account and data deletion</p>
          </div>

          <div className="space-y-8 text-white/60 text-sm leading-relaxed">

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                How to Request Account Deletion
              </h2>
              <p>
                To request the deletion of your NoiRock account and all associated data,
                please send an email to:
              </p>
              <p className="mt-3">
                <a
                  href="mailto:contact@noirock.app?subject=Account%20Deletion%20Request"
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  contact@noirock.app
                </a>
              </p>
              <p className="mt-3">
                Please include the email address associated with your account in your request.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                What Data Will Be Deleted
              </h2>
              <ul className="list-disc list-inside space-y-2 text-white/50">
                <li>Your account credentials and profile information</li>
                <li>Your activity data and usage history</li>
                <li>Push notification subscriptions</li>
                <li>Any associated session data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                Processing Time
              </h2>
              <p>
                Account deletion requests are processed within 30 days. You will receive
                a confirmation email once your account and data have been permanently deleted.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                Data Retention
              </h2>
              <p>
                Some data may be retained for legal or compliance purposes (e.g., financial
                transaction records) for up to 12 months after account deletion, as required
                by applicable law.
              </p>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-white/5 flex items-center justify-between">
            <span className="text-white/15 text-xs">{brandName} © 2026</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-white/20 text-xs hover:text-white/40 transition-colors no-underline">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/20 text-xs hover:text-white/40 transition-colors no-underline">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
